import { useState } from "react";
import { AppSidebar } from "./AppSidebar";
import { AppHeader } from "./AppHeader";
import { AppFooter } from "./AppFooter";
import { useResponsiveBreakpoint } from "@/hooks/use-responsive-breakpoint";
import { MobileSidebar } from "./MobileSidebar";
import { Button } from "@/components/ui/button";
import { Menu } from "lucide-react";
import { SidebarProvider, useSidebar } from "@/contexts/SidebarContext";

interface AppLayoutProps {
  children: React.ReactNode;
  title?: string;
}

function AppLayoutContent({ children, title }: AppLayoutProps) {
  const breakpoint = useResponsiveBreakpoint();
  const { collapsed, setCollapsed } = useSidebar();
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  const isMobile = breakpoint === 'mobile';
  const isTablet = breakpoint === 'tablet';
  const isDesktop = breakpoint === 'desktop';

  return (
    <div className="flex h-screen w-full min-w-0 bg-background">
      {/* Mobile: Hidden sidebar, use drawer */}
      {isMobile && (
        <MobileSidebar
          isOpen={mobileSidebarOpen}
          onOpenChange={setMobileSidebarOpen}
          trigger={<div />} // Empty trigger since we handle it in header
        />
      )}

      {/* Tablet & Desktop: Show collapsible sidebar */}
      {(isTablet || isDesktop) && (
        <AppSidebar 
          collapsed={collapsed} 
          onToggle={() => setCollapsed(!collapsed)}
          breakpoint={breakpoint}
        />
      )}

      <main className={`flex-1 flex flex-col min-h-0 transition-all duration-300 ${
        isMobile ? 'ml-0' : ''
      }`}>
        <AppHeader 
          title={title} 
          showMenuButton={isMobile}
          onMenuClick={() => setMobileSidebarOpen(true)}
        />
        <div className={`flex-1 overflow-y-auto overflow-x-hidden animate-fade-in ${
          isTablet && !collapsed 
            ? 'p-2 sm:p-3 pt-2 sm:pt-3' 
            : 'p-3 sm:p-4 md:p-6 pt-3 sm:pt-4 md:pt-6'
        }`}>
          {children}
        </div>
        <AppFooter />
      </main>
    </div>
  );
}

export function AppLayout({ children, title }: AppLayoutProps) {
  return (
    <SidebarProvider>
      <AppLayoutContent title={title}>
        {children}
      </AppLayoutContent>
    </SidebarProvider>
  );
}